import logger from 'lib/logger';
import { logFetchError } from 'lib/fetchUtils';
import {
  FormattedEligibility,
  FormattedGapElibility,
  MedicareApiSurveyAnswer,
  MedicareApiUser,
} from 'types/api';

import { UserHealthNeeds } from 'types/user';

const baseUrl = process.env.MEDICARE_API_BASE_URL;

const medicareApiHeaders = {
  Accept: 'application/vnd.picwell.v1+json',
  'x-api-key': process.env.MEDICARE_API_KEY,
} as HeadersInit;

export const createDefaultUser = async (state: string, zip: string) => {
  const defaultData = {
    age: 65,
    state_code: state,
    survey: [
      { answer: 'not_applicable', question_id: '1' },
      { answer: '0', question_id: '2' },
      { answer: '1_3', question_id: '3' },
      { answer: '0', question_id: '4' },
      { answer: '0', question_id: '5' },
    ],
    zip_code: zip,
  };

  logger.info({ action: 'browse', zip }, 'Medicare Api User Created');

  const response = await fetch(`${baseUrl}/medicare/user`, {
    body: JSON.stringify(defaultData),
    headers: medicareApiHeaders,
    method: 'POST',
  });

  if (!response.ok) {
    await logFetchError(response, 'Medicare API create default user failed');
  }

  return response;
};

export const updateUser = async (id: string, user: MedicareApiUser) => {
  logger.info({ id, action: 'update' }, 'Medicare Api User Updated');

  const response = await fetch(`${baseUrl}/medicare/user/${id}`, {
    body: JSON.stringify(user),
    headers: medicareApiHeaders,
    method: 'PUT',
  });

  if (!response.ok) {
    await logFetchError(response, 'Medicare API update user failed');
  }

  return response;
};

export const recommendPlans = async (
  plans: FormattedEligibility[] | FormattedGapElibility[],
  userId: string,
  year: number | string,
  type: string
) => {
  const url = `${baseUrl}/medicare/recommendation/${type}/${year}/${userId}`;

  const response = await fetch(url, {
    body: JSON.stringify(plans),
    headers: medicareApiHeaders,
    method: 'POST',
  });

  if (!response.ok) {
    await logFetchError(response, 'Medicare API recommend plans failed');
  }

  return response;
};

export const fetchGapPlanDetails = async (letter: string) => {
  const [formatted, type] = letter.split(' ');
  let formattedLetter = formatted;

  if (type === '(High Ded.)') {
    formattedLetter = `HIGH ${formatted}`;
  }

  const url = `${baseUrl}/medicare/medigap/plan_details/${formattedLetter}`;

  const response = await fetch(url, {
    headers: medicareApiHeaders,
    method: 'GET',
  });

  if (!response.ok) {
    await logFetchError(response, 'Medicare API fetch gap plan details failed');
  }

  return response;
};

const matchValues = (
  value: string,
  mapping: { match: string[]; value: string }[],
  notFound: string
) => {
  let formatted = notFound;

  mapping.forEach((element) => {
    if (element.match.includes(value as string)) {
      formatted = element.value;
    }
  });

  return formatted;
};

export const formatSurveyValues = (
  visits: UserHealthNeeds
): MedicareApiSurveyAnswer[] => {
  const pcp = matchValues(
    visits.primaryCare,
    [
      { match: ['', '0'], value: '0' },
      { match: ['1', '2', '3'], value: '1_3' },
    ],
    'gte_4'
  );

  const specialist = matchValues(
    visits.specialist,
    [
      { match: ['', '0'], value: '0' },
      { match: ['1', '2', '3', '4'], value: '1_4' },
      { match: ['5', '6', '7', '8', '9'], value: '5_9' },
    ],
    'gte_10'
  );

  const hospital = matchValues(
    visits.hospital,
    [
      { match: ['', '0'], value: '0' },
      { match: ['1'], value: '1' },
    ],
    'gte_2'
  );

  return [
    { question_id: '1', answer: 'very_important' },
    { question_id: '2', answer: 'gte_5' },
    { question_id: '3', answer: pcp },
    { question_id: '4', answer: specialist },
    { question_id: '5', answer: hospital },
  ];
};
